import {ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection} from '@angular/core';
import {provideRouter, withComponentInputBinding} from '@angular/router';

import {routes} from './app.routes';
import {provideClientHydration, withEventReplay} from '@angular/platform-browser';
import {provideTranslateService, TranslateLoader, TranslateService} from '@ngx-translate/core';
import {HttpClient, provideHttpClient, withInterceptors} from '@angular/common/http';
import {catchError, firstValueFrom, forkJoin, map, Observable, of} from 'rxjs';
import {registerLocaleData} from '@angular/common';
import localeEs from '@angular/common/locales/es';
import {errorInterceptor} from '@core/interceptors/error.interceptor';
import {providerInterceptor} from '@core/interceptors/provider.interceptor';
import {LanguageService} from '@core/services/language.service';
import {ThemeBootstrapService} from '@core/services/theme-bootstrap.service';
import {MAT_DATE_LOCALE, provideNativeDateAdapter} from '@angular/material/core';

class MultiHttpLoader implements TranslateLoader {
	constructor(private http: HttpClient, public resources: string[]) {
	}

	getTranslation(lang: string): Observable<any> {
		const requests = this.resources.map((path) => this.http.get(`${path}${lang}.json`));
		return forkJoin(requests).pipe(map((response) => Object.assign({}, ...response)));
	}
}

const httpLoaderFactory: (http: HttpClient) => MultiHttpLoader = (http: HttpClient) =>
	new MultiHttpLoader(http, [
		'/i18n/', // Ruta de la app
		'/assets/i18n/', // Ruta de la librería
	]);

registerLocaleData(localeEs);

export const appConfig: ApplicationConfig = {
	providers: [
		provideZoneChangeDetection({eventCoalescing: true}),
		provideRouter(routes, withComponentInputBinding()),
		provideHttpClient(withInterceptors([providerInterceptor, errorInterceptor])),
		provideTranslateService({
			fallbackLang: 'es',
			loader: {
				provide: TranslateLoader,
				useFactory: httpLoaderFactory,
				deps: [HttpClient],
			},
		}),
		provideAppInitializer(() => {
			const translate = inject(TranslateService);
			const language = inject(LanguageService);
			const themeBootstrap = inject(ThemeBootstrapService);
			language.autoResolveLanguage();
			// Tema global en login (y flash pre-auth). Tras /user/info se reaplica SE|global.
			return firstValueFrom(
				forkJoin({
					lang: translate.use(language.getLanguage()),
					theme: themeBootstrap.loadGlobalTheme().pipe(catchError(() => of(null))),
				}),
			);
		}),
		provideNativeDateAdapter(),
		{provide: MAT_DATE_LOCALE, useValue: 'es-ES'},
	]
};
